import { memo } from 'react'
import TemplateSettingsSheet from 'builder/components/TemplateSettingsSheet'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import { DownloadOptionsFooter } from 'builder/components/PreviewPanel'
import { useI18n } from 'builder/hooks/useI18n'
import { EditorPanelSwitcher } from 'builder/components/EditorPanelSwitcher'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/resumeEditor'
import { StartTailoringButton } from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import { TemplateCustomizationSheet } from '../TemplateCustomizationSheet'
import { DocumentCustomizationModalProps } from '../../types'
import { CUSTOMIZATION_MODAL_TEST_ID } from '../../constants'
import { Container, EditorPanelSwitcherWrapper, WrapperStartTailoringButton } from './styles'
import { useDocumentPreview } from './useDocumentPreview'
import PreviewToolbar from './PreviewToolbar'
import PreviewContent from './PreviewContent'

export const MobileDocumentCustomizationModal = memo((props: DocumentCustomizationModalProps) => {
  const {
    updateTemplate,
    currentDocument,
    type,
    isDownloading,
    onClose,
    showResumeDistributionModal,
    handleFontChange,
    handleChangeLayoutFormat,
    handleColorSelect,
    handleSpacingChange,
    handleSizeChange,
    renderPreview,
    handleDownloadDocument,
    isAiReviewMobileUiEnabled,
    editorPanelType,
    handleEditorPanelTypeChange,
    handleShareDocument,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
  } = props

  const { i18n } = useI18n()

  const isResume = isResumeDocument(currentDocument)

  const {
    isReadyToCoReg,
    isCoRegPopupOpen,
    toggleCoRegPopup,
    openCoReg,
    currentPage,
    totalPagesNumber,
    isSheetVisible,
    width,
    height,
    contentRef,
    previewBoxRef,
    previewRef,
    handlePreviewResize,
    handlePaginationStateChange,
    handlePreviousPageClick,
    handleNextPageClick,
    handleSheetOpen,
    handleSheetClose,
    isDataLoaded,
    templates,
    currentTemplate,
    colorSettings,
  } = useDocumentPreview(props)

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )
  return (
    <Container
      $isAiReviewMobileUiEnabled={isAiReviewMobileUiEnabled}
      data-testid={CUSTOMIZATION_MODAL_TEST_ID}
    >
      {isAiReviewMobileUiEnabled ? (
        <EditorPanelSwitcherWrapper>
          <EditorPanelSwitcher
            activePanelType={editorPanelType}
            handlePanelTypeChange={handleEditorPanelTypeChange}
            theme="darkest"
            items={[
              { text: i18n.t('builder.resume_editor.edit'), id: 'editor' },
              { text: i18n.t('builder.resume_editor.preview'), id: 'customization' },
              {
                text: i18n.t('builder.resume_editor.ai_review'),
                id: 'ai_review',
              },
            ]}
          />
        </EditorPanelSwitcherWrapper>
      ) : (
        <PreviewToolbar
          isResume={isResume}
          isDownloading={isDownloading}
          isSheetVisible={isSheetVisible}
          handleSheetOpen={handleSheetOpen}
          handleSheetClose={handleSheetClose}
          handleDownloadDocument={handleDownloadDocument}
          handleShareDocument={handleShareDocument}
          handleCombineDocuments={handleCombineDocuments}
          handleDownloadButtonMenuOpen={handleDownloadButtonMenuOpen}
          onClose={onClose}
          showResumeDistributionModal={showResumeDistributionModal}
          isReadyToCoReg={isReadyToCoReg}
          isCoRegPopupOpen={isCoRegPopupOpen}
          openCoReg={openCoReg}
          toggleCoRegPopup={toggleCoRegPopup}
        />
      )}

      <PreviewContent
        {...{
          contentRef,
          previewBoxRef,
          previewRef,
          type,
          width,
          height,
          isDataLoaded,
          currentDocument,
          currentTemplate,
          currentPage,
          totalPagesNumber,
          handlePreviousPageClick,
          handleNextPageClick,
          handlePaginationStateChange,
          handlePreviewResize,
          renderPreview,
        }}
      />

      {isAiReviewMobileUiEnabled && (
        <DownloadOptionsFooter
          currentDocument={currentDocument}
          showResumeDistributionModal={showResumeDistributionModal}
          handleDownloadDocument={handleDownloadDocument}
          handleShareDocument={handleShareDocument}
          handleCombineDocuments={handleCombineDocuments}
          isDarkBackground
          handleCustomizeClick={handleSheetOpen}
        />
      )}

      {autoTailoringFromPublicJobBoard.enabled ? (
        <WrapperStartTailoringButton>
          <StartTailoringButton />
        </WrapperStartTailoringButton>
      ) : (
        isDataLoaded &&
        isSheetVisible &&
        (isResume ? (
          <TemplateCustomizationSheet
            {...{
              resume: currentDocument,
              updateTemplate,
              onClose: handleSheetClose,
              handleFontChange,
              handleChangeLayoutFormat,
              handleColorSelect,
              handleSpacingChange,
              handleSizeChange,
            }}
          />
        ) : (
          <TemplateSettingsSheet
            type={type}
            isSpacingSupported={currentTemplate?.supportsSpacing}
            templates={templates}
            selectedTemplate={currentDocument.template}
            onColorChange={handleColorSelect}
            onTemplateChange={updateTemplate}
            onSpacingChange={handleSpacingChange}
            onClose={handleSheetClose}
            {...colorSettings}
          />
        ))
      )}
    </Container>
  )
})
